import React, { FunctionComponent, useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { BiSortAlt2 } from 'react-icons/bi';
import { ITaskSimple } from '../../../../app/resource/task-simple/task-simple.types';
import { IUser } from '../../../../app/resource/user/user.types';
import { IQuarterlyInitiativeProps } from './types';
import { useSelectWithParams } from '../../../../utils/selector.utils';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import { CircularAvatar } from '../../common/CustomAvatar';
import { meetingSimpleSelectors } from '../../../../app/resource/meeting-simple/meeting-simple.selectors';

const QuarterlyInitiatives: FunctionComponent<IQuarterlyInitiativeProps> = ({ goalList, showUsers = false }) => {
  const [sortField, setSortField] = useState<'status' | 'dueDate' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const toggleSort = (field: typeof sortField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection('asc');
    } else {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
    }
  };

  const sortedGoals = useMemo(() => {
    if (!sortField || !sortDirection) {
      return sortBy(goalList, [
        (goal) => {
          if (goal.status === '0') {
            return 0;
          }
          if (goal.status === '2') {
            return 1;
          }
          return 99;
        },
        'dueDate',
      ]);
    }

    let fieldPath: string;

    switch (sortField) {
      // case 'status':
      //   fieldPath = 'status';
      //   break;
      case 'dueDate':
        fieldPath = 'dueDate';
        break;
      default:
        fieldPath = '';
    }

    const sorted = sortBy(goalList, [fieldPath]);
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [goalList, sortField, sortDirection]);

  const renderSortIcon = (column: typeof sortField) => {
    if (sortField !== column) {
      return <BiSortAlt2 size={18} />;
    }

    switch (sortDirection) {
      case 'asc':
        return <i className="icon-sort-alt-up"></i>;
      case 'desc':
        return <i className="icon-sort-alt-down"></i>;
      default:
        return <BiSortAlt2 size={18} />;
    }
  };

  return (
    <>
      <div className="panel my-1 p-2">
        <div className="aic flex theme-gradient-2 mb-2 panel px-2 py-1">
          <h2 className="white bold">Quarterly Initiatives</h2>
        </div>
        <div className="border-radius-1 lightGray-bg p-2 container justify-center">
          <div className="table-container kpi-settings-table w100" role="table" aria-label="KPIs settings">
            {sortedGoals.length === 0 ? (
              <div className="f-2 text-center">This member has no goals assigned in the current period</div>
            ) : (
              <>
                <div className="flex-table header panel mb-1 aic py-0">
                  {showUsers && (
                    <div className="px-1" style={{ width: 200 }}>
                      USER
                    </div>
                  )}
                  <div className="px-1" style={{ flex: 1 }}>
                    TITLE
                  </div>
                  {sortedGoals[0].meetingIds && (
                    <div className="px-1" style={{ width: 200 }}>
                      MEETING
                    </div>
                  )}
                  <div
                    className="flex align-center px-1 mr-1 pointer"
                    style={{ width: 110 }}
                    onClick={() => toggleSort('dueDate')}
                  >
                    <div className="text-left">DUE DATE</div>
                    <span className=" mr-1">{renderSortIcon('dueDate')}</span>
                  </div>
                  <div className="flex align-center px-1 mr-1" style={{ width: 75 }}>
                    <div className="text-left">STATUS</div>
                  </div>
                </div>
                <>
                  {sortedGoals.map((goal) => (
                    <div className="panel justify-between flex-table flex p-2 align-center" key={goal.id}>
                      <GoalDetail goal={goal} userId={showUsers ? goal.userId : undefined} />
                    </div>
                  ))}
                </>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const GoalDetail = ({ goal, userId }: { goal: ITaskSimple; userId?: IUser['id'] }) => {
  const user = useSelectWithParams(userSelectors.selectResourceById, userId ?? '');

  const meetingIds = goal.meetingIds;
  const meetings = useSelectWithParams(meetingSimpleSelectors.selectResourcesById, meetingIds ?? []);
  const meeting = meetings[0];

  const badgeInfo = useMemo(() => {
    const options = {
      '0': {
        text: 'On Track',
        colorClass: 'lightBlue-bg blue',
      },
      '1': {
        text: (
          <>
            <i className="icon-check"></i> Done
          </>
        ),
        colorClass: 'lightGreen-bg green',
      },
      '2': {
        text: 'Off Track',
        colorClass: 'lightYellow-bg yellow',
      },
      '3': {
        text: 'Incomplete',
        colorClass: 'lightRed-bg red',
      },
      '4': {
        text: 'Overdue',
        colorClass: 'lightRed-bg red',
      },
    };
    return options[goal.status];
  }, [goal.status]);

  const dueDate = goal.dueDate.split(' ')[0];
  return (
    <>
      {user && (
        <div className="px-1 flex align-center" style={{ width: 200 }}>
          <CircularAvatar
            src={user.image}
            title={`${user.firstName} ${user.lastName}`}
            classes="small-avatar mr-05"
            cursorPointer={false}
          />
          <div className="account-item-desc">
            <h3 className="account-item-title">{`${user.firstName} ${user.lastName}`}</h3>
          </div>
        </div>
      )}
      <div className="flex flex-row px-1" style={{ flex: 1 }}>
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{goal.title}</div>
      </div>
      {meeting && (
        <div className="flex align-center px-1" style={{ width: 200 }}>
          <CircularAvatar
            src={meeting.image}
            title={meeting.title}
            color={meeting.color}
            textSizeRatio={2.5}
            classes="account-item-image f-2 bold"
          />
          <div style={{ marginLeft: 5 }}>
            <h3>{meeting.title}</h3>
          </div>
        </div>
      )}
      <div style={{ width: 110 }}>{dueDate}</div>
      <div className={`badge ${badgeInfo.colorClass} mr-1 px-1`} style={{ width: 75 }}>
        {badgeInfo.text}
      </div>
    </>
  );
};

export default QuarterlyInitiatives;
